import { useState } from 'react';
import toast from 'react-hot-toast';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useSendBulkMachineCommandsMutation } from '@/store/api/services/machines/machines';

import type { CommandResult } from '@/types';
import { ClickableText, type ModalChildrenProps } from '@bitstopco/bitstop-theme';

const BulkRestartMachines = ({ hide, props }: ModalChildrenProps) => {
  const [results, setResults] = useState<CommandResult[]>([]);
  const [sendBulkCommand, { isLoading, isSuccess }] = useSendBulkMachineCommandsMutation();

  const handleSubmit = async () => {
    try {
      const { data } = await sendBulkCommand({
        atm_ids: props.selectedAtms.map(({ id }) => id),
        command: 'Restart',
      }).unwrap();

      const results = data?.results || [];
      setResults(results);
      const successCount = results?.filter((res: any) => res.success).length || 0;

      if (successCount === results.length) {
        toast.success(`${successCount} machine(s) restarted successfully.`);
      } else {
        const atmErrList = results
          .filter((atm: any) => atm.error)
          .map((atm: any) => `${atm.atm_id}`);
        toast.error(
          `${results.length - successCount} machine(s) failed to restart.\n${atmErrList.join(', ')}`,
        );
      }
    } catch (err) {
      toast.error(`An unexpected error occurred during bulk restart: ${err}`);
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" mb={1}>
            Are you sure you would like to restart the selected machines?
          </Typography>
          <Typography variant="body1">This restart action cannot be undone.</Typography>
        </Box>

        <Divider />

        {/* Show Preview Table of ATM IDs before the restart is triggered */}
        {!isSuccess && (
          <Box mt={2}>
            <Typography variant="subtitle1" mb={1}>
              The following ATM IDs will receive the restart command:
            </Typography>
            <TableContainer sx={{ opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.3s' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ATM ID</TableCell>
                    <TableCell>ATM Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {props?.selectedAtms?.map((atm: any) => (
                    <TableRow key={atm.id}>
                      <TableCell>
                        <ClickableText
                          fixIcon
                          text={atm.id.toString()}
                          clipboardValue={atm.id.toString()}
                          name="Machine ID"
                          to={`${window.location.origin}/machine/${atm.id}`}
                        />
                      </TableCell>
                      <TableCell>{atm.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {isLoading && (
                <CircularProgress
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
            </TableContainer>
          </Box>
        )}

        {/* Show Results Table only after the restart command is completed */}
        {isSuccess && (
          <Box mt={3}>
            <Typography variant="h6">Restart Results</Typography>
            <TableContainer sx={{ opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.3s' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ATM ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map(({ atm_id, success, error }: any) => (
                    <TableRow key={atm_id}>
                      <TableCell>
                        <ClickableText
                          fixIcon
                          text={atm_id.toString()}
                          clipboardValue={atm_id.toString()}
                          name="Machine ID"
                          to={`${window.location.origin}/machine/${atm_id}`}
                        />
                      </TableCell>
                      <TableCell>{success ? 'Success' : 'Failed'}</TableCell>
                      <TableCell>{error || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {isLoading && (
                <CircularProgress
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {!isSuccess && (
          <>
            <Button onClick={hide}>Cancel</Button>
            <LoadingButton variant="contained" onClick={handleSubmit} loading={isLoading}>
              Restart
            </LoadingButton>
          </>
        )}
        {isSuccess && (
          <Button fullWidth onClick={hide}>
            Back to Machines
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default BulkRestartMachines;
